import { createEnrichmentIsland } from "@/utils/islands";
import { hideSpinner, showSpinner } from "@sitecorecomponents/BasicContent/Modal";
import { clearSession } from "../Store/tokenstore";
import { openLogin } from "../Login/login";
import { openForgotPassword } from "../ForgotPassword/forgotpassword";
import { passwordRules, requiredRule, setupFieldValidation, setupPasswordChecklist, setupPasswordToggle } from "../formValidation";
import { showFormMessage } from "../formMessages";
import {
    resetPasswordMutation,
    ResetPasswordMutationResult, ResetPasswordResult
} from "@sitecorecomponents/Users/ResetPassword/resetpassword.query";
import {fetchApiGraphQL, GraphQLError} from "@/utils/fetcher";

function wireExpiredLinks(form: HTMLFormElement) {
  form.querySelector<HTMLAnchorElement>('#open-forgot-password')?.addEventListener('click', e => {
    e.preventDefault();
    openForgotPassword();
  });

  form.querySelector<HTMLAnchorElement>('#open-login')?.addEventListener('click', e => {
    e.preventDefault();
    openLogin();
  });
}

const redirectDelayMs = 3000;

function readToken(): string | null {
  return new URLSearchParams(document.location.search).get('token');
}

function showExpired(form: HTMLFormElement) {
  form.querySelectorAll<HTMLElement>('[data-reset-step="form"]')
    .forEach(el => el.classList.add('hidden'));

  form.querySelectorAll<HTMLElement>('[data-reset-step="expired"]')
    .forEach(el => el.classList.replace('hidden', 'flex'));
  showFormMessage(form, null);
}

function showSuccess(form: HTMLFormElement) {
  form.querySelectorAll<HTMLElement>('[data-reset-step="form"]')
    .forEach(el => el.classList.add('hidden'));

  form.querySelectorAll<HTMLElement>('[data-reset-step="success"]')
    .forEach(el => el.classList.replace('hidden', 'flex'));
  showFormMessage(form, null);
}

function showServerError(form: HTMLFormElement, message: string | undefined) {
  const target = form.querySelector<HTMLElement>('[data-form-message="password-rejected"]');
  if (!target || !message) {
    showFormMessage(form, 'request-failed');
    return;
  }

  target.textContent = message;
  showFormMessage(form, 'password-rejected');
}

function redirectToLogin() {
  clearSession();

  document.location.href = '/';
}
async function resetPassword(token: string, password: string): Promise<ResetPasswordResult> {
    let result: ResetPasswordMutationResult;
    try {
        result = await fetchApiGraphQL<ResetPasswordMutationResult>(
            resetPasswordMutation,
            { token, password },
            true
        );
    } catch (err) {
        const message = (err as GraphQLError)?.errors?.[0]?.message ?? '';
        const lower = message.toLowerCase();

        if (lower.includes('token')) {
            return { success: false, reason: 'invalid-token' };
        }
        if (lower.includes('password')) {
            return { success: false, reason: 'password-rejected', errorMessage: message };
        }
        return { success: false, reason: 'request-failed' };
    }

    if (!result?.data?.resetPassword) {
        return { success: false, reason: 'invalid-token' };
    }

    return { success: true };
}
async function handleSubmit(e: SubmitEvent, form: HTMLFormElement) {
  e.preventDefault();

  const token = readToken();
  if (!token) {
    showExpired(form);
    return;
  }

  const password = form.querySelector<HTMLInputElement>('#Password')?.value ?? '';

  showFormMessage(form, null);

  if (passwordRules.some(rule => !rule.test(password))) {
    showFormMessage(form, 'password-complexity');
    return;
  }

  let result: ResetPasswordResult;
  try {
    showSpinner();
    result = await resetPassword(token, password);
  } finally {
    hideSpinner();
  }

  if (result.success) {
    showSuccess(form);
    window.setTimeout(redirectToLogin, redirectDelayMs);
    return;
  }

  if (result.reason === 'invalid-token') {
    showExpired(form);
    return;
  }

  if (result.reason === 'password-rejected') {
    showServerError(form, result.errorMessage);
    return;
  }

  showFormMessage(form, 'request-failed');
}

export default function init() {
  createEnrichmentIsland('.reset-password-form', elm => {
    const form = elm as HTMLFormElement;

    const password = form.querySelector<HTMLInputElement>('#Password');
    if (password) {
      setupFieldValidation(password, [requiredRule]);
      setupPasswordChecklist(password);
      setupPasswordToggle(password);
    }

    wireExpiredLinks(form);

    if (!readToken()) {
      showExpired(form);
      return;
    }

    form.addEventListener('submit', e => handleSubmit(e as SubmitEvent, form));
  });
}
